import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { httpClient } from '../../api/httpClient';

// Async thunks для работы с API
export const fetchUserSettings = createAsyncThunk(
    'settings/fetchUserSettings',
    async (_, { rejectWithValue }) => {
        try {
            const response = await httpClient.get('/settings/');
            
            if (!response.ok) {
                throw new Error('Failed to fetch settings');
            }
            
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const updateSetting = createAsyncThunk(
    'settings/updateSetting',
    async ({ key, value }, { rejectWithValue }) => {
        try {
            const response = await httpClient.put(`/settings/${key}`, { value });
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('updateSetting - ошибка сервера:', errorText);
                throw new Error(`Failed to update setting: ${response.status} ${response.statusText}`);
            }
            
            const result = await response.json();
            return { key, value };
        } catch (error) {
            console.error('updateSetting - ошибка:', error);
            return rejectWithValue(error.message);
        }
    }
);

export const updateMultipleSettings = createAsyncThunk(
    'settings/updateMultipleSettings',
    async (settings, { rejectWithValue }) => {
        try {
            const response = await httpClient.post('/settings/batch', { settings });
            
            if (!response.ok) {
                throw new Error('Failed to update settings');
            }
            
            return settings;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const initialState = {
    settings: {},
    loading: false,
    error: null,
    initialized: false,
};

const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        // Локальное обновление настроек (без API)
        setSetting: (state, action) => {
            const { key, value } = action.payload;
            state.settings[key] = value;
        },
        
        // Установка нескольких настроек локально
        setMultipleSettings: (state, action) => {
            state.settings = { ...state.settings, ...action.payload };
        },
        
        // Сброс состояния
        resetSettings: (state) => {
            state.settings = {};
            state.loading = false;
            state.error = null;
            state.initialized = false;
        },
        
        // Очистка ошибки
        clearError: (state) => {
            state.error = null;
        },
        
        // Установка состояния инициализации
        setInitialized: (state) => {
            state.initialized = true;
        },
    },
    extraReducers: (builder) => {
        // fetchUserSettings
        builder
            .addCase(fetchUserSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.settings = action.payload;
                state.initialized = true;
            })
            .addCase(fetchUserSettings.rejected, (state, action) => {
                state.loading = false;
                // Не устанавливаем ошибку, если токен отсутствует
                if (action.payload !== 'No authentication token') {
                    state.error = action.payload;
                }
                state.initialized = true; // Помечаем как инициализированное, чтобы не повторять запросы
            })
            
        // updateSetting
        builder
            .addCase(updateSetting.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateSetting.fulfilled, (state, action) => {
                state.loading = false;
                const { key, value } = action.payload;
                state.settings[key] = value;
            })
            .addCase(updateSetting.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
        // updateMultipleSettings
        builder
            .addCase(updateMultipleSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateMultipleSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.settings = { ...state.settings, ...action.payload };
            })
            .addCase(updateMultipleSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { 
    setSetting, 
    setMultipleSettings, 
    resetSettings, 
    clearError,
    setInitialized
} = settingsSlice.actions;

// Селекторы
export const selectSettings = (state) => state.settings.settings;
export const selectSetting = (key) => (state) => state.settings.settings[key];
export const selectSettingsLoading = (state) => state.settings.loading;
export const selectSettingsError = (state) => state.settings.error;
export const selectSettingsInitialized = (state) => state.settings.initialized;

export default settingsSlice.reducer; 